import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button-new";
import { Input } from "./ui/input";
import { useState, useEffect } from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

interface MonthSelectorProps {
    selectedMonth: string; // Format: YYYY-MM
    onMonthChange: (month: string) => void;
    onPreviousMonth: () => void;
    onNextMonth: () => void;
}

export function MonthSelector({
    selectedMonth,
    onMonthChange,
    onPreviousMonth,
    onNextMonth,
}: MonthSelectorProps) {
    const [year, month] = selectedMonth.split("-").map(Number);
    const [selectedYear, setSelectedYear] = useState<string>(year.toString());
    const [selectedMonthNum, setSelectedMonthNum] = useState<string>(
        month.toString().padStart(2, "0")
    );
    const [customYear, setCustomYear] = useState<string>(year.toString());

    // Update local state when props change
    useEffect(() => {
        const [newYear, newMonth] = selectedMonth.split("-").map(Number);
        setSelectedYear(newYear.toString());
        setSelectedMonthNum(newMonth.toString().padStart(2, "0"));
        setCustomYear(newYear.toString());
    }, [selectedMonth]);

    // Month options
    const monthOptions = [
        { value: "01", label: "January" },
        { value: "02", label: "February" },
        { value: "03", label: "March" },
        { value: "04", label: "April" },
        { value: "05", label: "May" },
        { value: "06", label: "June" },
        { value: "07", label: "July" },
        { value: "08", label: "August" },
        { value: "09", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
    ];

    // Generate year options (current year and 10 years back)
    const getYearOptions = () => {
        const options = [];
        const currentYear = new Date().getFullYear();

        for (let i = 0; i <= 10; i++) {
            const yearValue = (currentYear - i).toString();
            options.push({ value: yearValue, label: yearValue });
        }

        return options;
    };

    const yearOptions = getYearOptions();

    const handleMonthChange = (value: string) => {
        setSelectedMonthNum(value);
        onMonthChange(`${selectedYear}-${value}`);
    };

    const handleYearChange = (value: string) => {
        setSelectedYear(value);
        setCustomYear(value);
        onMonthChange(`${value}-${selectedMonthNum}`);
    };

    const handleCustomYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            // Only allow digits
            setCustomYear(value);
        }
    };

    const handleCustomYearBlur = () => {
        if (customYear && parseInt(customYear) > 0) {
            setSelectedYear(customYear);
            onMonthChange(`${customYear}-${selectedMonthNum}`);
        } else {
            // Reset to current year if invalid
            setCustomYear(selectedYear);
        }
    };

    const handleCustomYearKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e.key === "Enter") {
            handleCustomYearBlur();
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-1">
            <Button
                variant="default"
                size="icon"
                onClick={onPreviousMonth}
                aria-label="Previous month"
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
                {/* Month selector */}
                <Select
                    value={selectedMonthNum}
                    onValueChange={handleMonthChange}
                >
                    <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                        {monthOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* Year selector */}
                <Select value={selectedYear} onValueChange={handleYearChange}>
                    <SelectTrigger className="w-[90px]">
                        <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                        <div className="p-2">
                            <Input
                                type="text"
                                value={customYear}
                                onChange={handleCustomYearChange}
                                onBlur={handleCustomYearBlur}
                                onKeyDown={handleCustomYearKeyDown}
                                className="mb-2"
                                placeholder="Enter year"
                                maxLength={4}
                            />
                        </div>
                        {yearOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <Button
                variant="default"
                size="icon"
                onClick={onNextMonth}
                aria-label="Next month"
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    );
}
